import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { PAYMENT, Payment } from '../../../../models/Schedule';
import HeadTitle from '../../../../components/HeadTitle';
import { Card } from '../Card';
import { useContextSelector } from 'use-context-selector';
import { PaymentContext } from '../../../../contexts/PaymentContext';
import { PAYMENT_TYPE } from '../../../../utils/constants';
import HealthPlanForm from '../../../../components/HealthPlanForm';
import { ScheduleContext } from '../../../../contexts/ScheduleContext';
import { ModuleContext } from '../../../../contexts/ModuleContext';
import { RouterContext } from '../../../../contexts/RouterContext';
import { CREATE_SCHEDULE_APPOINTMENTS_TYPES } from '../../../../utils/enums/appointment-types';
import { UnitHealthPlans } from '../../../../models/Specialist';
import Search, { IOptionsList } from '../Search';
import { CardWrapper, PaymentMethodContainer, TitleSection, Info } from './styles';
import { PAGES } from '../../../../Router/pages';

import { ReactComponent as WarningIconYellow } from '../../../../assets/warning-yellow.svg';

const EligibilityDefaultIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/helth-plan.svg';
const InsuranceCardIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/helth-plan.svg';
const MoneyIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/particularDollar.svg';

export type PaymentMethodProps = {
  payment: Payment | undefined;
  specialistMedicalCare: UnitHealthPlans | null;
  setPayment: (payment: Payment) => void;
  handlePayment: (paymentMethod: string | Payment) => void;
  setIsModalHealthInsuranceUnavailableVisible: (new_value: boolean) => void;
};

interface IPlans {
  external_id?: string;
  healthPlanId: string;
  planName: string;
  healthInsuranceId: string;
}

export type HealthPlanData = {
  insuranceName: string;
  plan: string;
  benefiarcyNumber: string;
};

interface IHealthInsuranceList {
  external_id?: string;
  insuranceName: string;
  healthInsuranceId: string;
}
interface IPlansList {
  external_id?: string;
  healthPlanId: string;
  planName: string;
  healthInsuranceId: string;
}

const handleSearchFilter = (filterList: any[], value: string, filterKey: string) => {
  return filterList.filter(txt => {
    if (
      (txt[filterKey] as string)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .includes(
          value
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        )
    )
      return txt;

    return;
  });
};

export const PaymentMethod = ({
  payment,
  setPayment,
  specialistMedicalCare,
  handlePayment,
  setIsModalHealthInsuranceUnavailableVisible
}: PaymentMethodProps) => {
  const healthPlanForm = useFormik({
    initialValues: {
      healthInsurance: {
        id: '',
        name: '',
        external_id: null
      },
      healthPlan: {
        id: '',
        name: '',
        external_id: null
      }
    },
    onSubmit: async values => {
      setPayment({
        ...payment,
        ...values
      });
    }
  });

  const { eligibilityPlans, clearEligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const { selectedItemToSchedule } = useContextSelector(ModuleContext, state => state);
  const { scheduleSpecialistSelected, changeScheduleSpecialist } = useContextSelector(ScheduleContext, state => state);
  const { goBack, goTo } = useContextSelector(RouterContext, state => state);

  const [showSearchWrapper, setShowSearchWrapper] = React.useState(false);
  const [insuranceData, setInsuranceData] = useState<{ insurances: IHealthInsuranceList[]; healthPlans: IPlans[] }>({
    insurances: [] as IHealthInsuranceList[],
    healthPlans: [] as IPlans[]
  });
  const [resultInsuranceList, setResultInsuranceList] = useState<IHealthInsuranceList[]>([]);
  const [filterInputSearch, setFilterInputSearch] = React.useState('');
  const [originalPlanList, setOriginalPlanList] = React.useState<IPlansList[]>([]);
  const [resultPlanList, setResultPlanList] = React.useState<IPlansList[]>([]);
  const [searchType, setSearchType] = useState<'healthInsurance' | 'healthPlan'>('healthInsurance');

  useEffect(() => {
    if (specialistMedicalCare) handleSetHealhPlans(specialistMedicalCare);
  }, [specialistMedicalCare]);

  useEffect(() => {
    if (healthPlanForm.isValid) healthPlanForm.submitForm();
  }, [healthPlanForm.isValid, healthPlanForm.values]);

  const handleSetHealhPlans = (data: UnitHealthPlans) => {
    const insurancesMap = new Map();
    const healthPlansMap = new Map();

    data.health_plans.forEach(healthPlan => {
      if (healthPlan.health_plan.health_insurance.name.toUpperCase() === 'PARTICULAR') return;

      insurancesMap.set(healthPlan.health_plan.health_insurance.id, {
        external_id: healthPlan.health_plan.health_insurance.external_id,
        insuranceName: healthPlan.health_plan.health_insurance.name,
        healthInsuranceId: healthPlan.health_plan.health_insurance.id
      });
      healthPlansMap.set(healthPlan.health_plan.id, {
        external_id: healthPlan.health_plan.external_id,
        healthPlanId: healthPlan.health_plan.id,
        planName: healthPlan.health_plan.description,
        healthInsuranceId: healthPlan.health_plan.health_insurance.id
      });
    });

    const insurances = [...insurancesMap.values()];
    const healthPlans = [...healthPlansMap.values()];

    setResultInsuranceList(insurances);
    setInsuranceData({
      insurances,
      healthPlans
    });
  };

  const particularValue = React.useMemo(() => {
    if (!specialistMedicalCare) return null;

    const particularInsurance = specialistMedicalCare.health_plans.find(
      ({ health_plan }) => health_plan.description === 'PARTICULAR'
    );

    if (scheduleSpecialistSelected.attendanceType === 'PRESENTIAL') {
      if (!particularInsurance) return null;
      return particularInsurance.price;
    }

    if (!specialistMedicalCare.telemedicine_consultation.accept_telemedicine) return null;

    return specialistMedicalCare.telemedicine_consultation.price;
  }, [scheduleSpecialistSelected, specialistMedicalCare]);

  const hasHealthInsuranceEnabled = React.useMemo(() => {
    if (
      scheduleSpecialistSelected.attendanceType === 'TELEMEDICINE' &&
      specialistMedicalCare?.telemedicine_consultation.accept_health_care
    ) {
      const { telemedicine_consultation } = specialistMedicalCare;
      return telemedicine_consultation?.office_telemedicine_health_insurance?.length > 0;
    } else if (scheduleSpecialistSelected.attendanceType === 'PRESENTIAL' && specialistMedicalCare?.health_plans) {
      const { health_plans } = specialistMedicalCare;
      return health_plans.length > 0;
    }
    return false;
  }, [specialistMedicalCare, scheduleSpecialistSelected]);

  const handleChangeValue = (value: string, inputType: 'healthPlan' | 'healthInsurance') => {
    const cleanedText = value.replace(/ {2,}/g, ' ');
    if (cleanedText.length !== 0 && cleanedText[0] === ' ') return;

    setFilterInputSearch(value);
    if (inputType === 'healthInsurance') {
      const { insurances } = insuranceData;

      if (!value.length) return setResultInsuranceList(insurances);

      const filteredInsurances = handleSearchFilter(insurances, value, 'insuranceName');

      setResultInsuranceList(filteredInsurances);
    } else if (inputType === 'healthPlan') {
      if (!value.length) return setResultPlanList(originalPlanList);

      const filteredHealthPlans = handleSearchFilter(originalPlanList, value, 'planName');

      setResultPlanList(filteredHealthPlans);
    }
  };

  const handleOptionsList = () => {
    if (searchType === 'healthInsurance')
      return resultInsuranceList.map(item => ({
        id: item.healthInsuranceId,
        name: item.insuranceName
      }));

    return resultPlanList.map(item => ({
      id: item.healthPlanId,
      name: item.planName
    }));
  };

  const handleClickSearchResult = (option: IOptionsList) => {
    setShowSearchWrapper(false);
    handleClearList();

    if (searchType === 'healthInsurance') {
      const findSelectedInsurance = resultInsuranceList.find(insurance => insurance.healthInsuranceId === option.id);

      if (!findSelectedInsurance) return;

      healthPlanForm.setFieldValue(searchType, {
        id: findSelectedInsurance.healthInsuranceId,
        name: findSelectedInsurance.insuranceName,
        external_id: findSelectedInsurance.external_id
      });
      healthPlanForm.setFieldValue('healthPlan', {
        id: '',
        name: '',
        external_id: null
      });

      const filteredInsurancePlans = insuranceData.healthPlans.filter(plan => plan.healthInsuranceId === option.id);

      setOriginalPlanList(filteredInsurancePlans);
      setResultPlanList(filteredInsurancePlans);
    } else {
      const findSelectedPlan = resultPlanList.find(plan => plan.healthPlanId === option.id);

      if (!findSelectedPlan) return;

      healthPlanForm.setFieldValue(searchType, {
        id: findSelectedPlan.healthPlanId,
        name: findSelectedPlan.planName,
        external_id: findSelectedPlan.external_id
      });
    }
  };

  const handleClearList = () => {
    setFilterInputSearch('');
    setResultInsuranceList(insuranceData.insurances);
    setResultPlanList(originalPlanList);
  };

  const handleOnClickParticular = () => {
    if (!particularValue) return;

    setPayment({
      type: PAYMENT.PARTICULAR,
      price: particularValue
    });
    healthPlanForm.setFieldValue('healthInsurance', {
      id: '',
      name: '',
      external_id: null
    });
    healthPlanForm.setFieldValue('healthPlan', {
      id: '',
      name: '',
      external_id: null
    });
  };

  return (
    <PaymentMethodContainer showSearchWrapper={showSearchWrapper}>
      {!showSearchWrapper ? (
        <>
          <HeadTitle
            goBack={() => {
              changeScheduleSpecialist({
                ...scheduleSpecialistSelected,
                payment: {
                  type: PAYMENT_TYPE.PARTICULAR
                }
              });
              clearEligibilityPlans();
              goBack();
            }}
            title="Escolha a forma de pagamento"
          />

          {selectedItemToSchedule === 'SPECIALIST' ? (
            <CardWrapper>
              {payment?.type === PAYMENT.PARTICULAR && (
                <Info
                  info={'Realize o pagamento no dia da consulta. Verifique as opções aceitas pelo profissional.'}
                  icon={<WarningIconYellow />}
                  type={'WARNING'}
                />
              )}

              {particularValue && (
                <Card
                  icon={MoneyIcon}
                  title="Particular"
                  /*    subtitle={moneyFormat(String(particularValue))} */
                  onClick={handleOnClickParticular}
                  checked={payment?.type === PAYMENT.PARTICULAR}
                />
              )}

              {hasHealthInsuranceEnabled && (
                <Card
                  icon={InsuranceCardIcon}
                  title="Plano de saúde"
                  onClick={() => {
                    // setPayment({
                    //   type: PAYMENT.HEALTH_INSURANCE
                    // });
                    setIsModalHealthInsuranceUnavailableVisible(true);
                  }}
                  checked={payment?.type === PAYMENT.HEALTH_INSURANCE}
                />
              )}

              {payment?.type === PAYMENT.HEALTH_INSURANCE && (
                <HealthPlanForm
                  insuranceName={healthPlanForm.values.healthInsurance.name}
                  planName={healthPlanForm.values.healthPlan.name}
                  onClickInput={type => {
                    setShowSearchWrapper(true);
                    setSearchType(type);
                    setFilterInputSearch('');
                  }}
                />
              )}
            </CardWrapper>
          ) : (
            eligibilityPlans && (
              <CardWrapper>
                <TitleSection>Planos cadastrados</TitleSection>
                {eligibilityPlans.map((plan, index) => {
                  return (
                    <Card
                      key={index}
                      icon={plan.image ? plan.image : EligibilityDefaultIcon}
                      title={plan.nome}
                      onClick={() =>
                        handlePayment({
                          type: PAYMENT_TYPE.B2B,
                          eligibility: {
                            idInsurance: plan.id_convenio,
                            idCustomer: plan.id_cliente,
                            codInsurance: plan.cod_convenio,
                            codPlan: plan.cod_plano,
                            codOffice: plan.cod_escritorio,
                            plan: plan.nome,
                            image: plan.image
                          }
                        })
                      }
                      checked={payment?.eligibility?.plan === plan.nome && payment?.type === PAYMENT_TYPE.B2B}
                    />
                  );
                })}
              </CardWrapper>
            )
          )}
        </>
      ) : (
        <Search
          optionsList={handleOptionsList()}
          handleChangeValue={value => handleChangeValue(value, searchType)}
          filterInputSearch={filterInputSearch}
          handleClickResult={handleClickSearchResult}
          changeContext={() => {
            setShowSearchWrapper(!showSearchWrapper);
          }}
          handleClear={handleClearList}
          emptyListFirstButton={
            particularValue
              ? () => {
                  setShowSearchWrapper(false);
                  handleClearList();
                  setPayment({
                    type: PAYMENT.PARTICULAR,
                    price: particularValue ?? ''
                  });
                }
              : null
          }
          emptyListSecondButton={
            hasHealthInsuranceEnabled
              ? () => {
                  goTo(PAGES.SPECIALIST_LIST.id);
                }
              : null
          }
          rounded
        />
      )}
    </PaymentMethodContainer>
  );
};
